/**
 * Conta, identidade, depósito e saque por Pix.
 *
 * Nenhuma decisão sobre dinheiro acontece aqui. Quem diz se a conta pode
 * depositar é o banco, em `fn_deposit_precheck`; quem credita saldo é a
 * conciliação, quando o pagamento realmente entra. Este arquivo pergunta
 * e mostra.
 *
 * Repare no que falta em `pedirSaque`: destino. Não é omissão da
 * interface — a chave é o CPF do próprio titular, derivado no servidor, e
 * não existe caminho pelo qual a tela escolha para onde o dinheiro vai.
 */
import { baseDaApi } from './presenca.ts';

export interface EstadoConta {
  readonly identityDeclared: boolean;
  readonly kycStatus: string;
  readonly accountStatus: string;
  readonly selfExcludedUntil: string | null;
  readonly dailyDepositLimitCents: number | null;
  readonly canUseRealMoney: boolean;
  readonly blockedReason: string | null;
  readonly balance: { readonly availableCents: number; readonly escrowCents: number };
}

export interface Cobranca {
  readonly txid: string;
  readonly brCode: string;
  readonly amountCents: number;
  readonly expiraSegundos: number;
}

export interface SituacaoCobranca {
  readonly txid: string;
  readonly status: string;
  readonly amountCents: number;
  readonly paidAmountCents: number | null;
  readonly brCode: string | null;
  readonly expiresAt: string;
  readonly paidAt: string | null;
}

/** Situação do saque, do ponto de vista de quem saca. */
export interface EstadoSaque {
  readonly podeSacar: boolean;
  readonly motivo: string | null;
  readonly disponivelCents: number;
  readonly limites: {
    readonly minimoCents: number;
    readonly maximoCents: number;
    readonly maximoDiarioCents: number;
    readonly intervaloMinutos: number;
  };
  /** CPF mascarado, só para a pessoa reconhecer para onde vai. */
  readonly destinoMascarado: string | null;
  readonly emAndamento: {
    readonly id: string;
    readonly amount_cents: number;
    readonly status: string;
    readonly requested_at: string;
  } | null;
  readonly historico: {
    readonly depositadoCents: number;
    readonly apostadoCents: number;
    readonly sacadoCents: number;
  };
}

export type Resultado<T> =
  | { readonly estado: 'ok'; readonly dado: T }
  | { readonly estado: 'recusado'; readonly codigo: string; readonly mensagem: string }
  | { readonly estado: 'indisponivel'; readonly mensagem: string };

const MOTIVOS: Record<string, string> = {
  IDENTITY_REQUIRED: 'Informe data de nascimento e CPF antes de depositar.',
  IDENTITY_ALREADY_DECLARED: 'Seus dados já foram informados. Para corrigir, fale com o suporte.',
  CPF_ALREADY_USED: 'Esse CPF já tem conta. Cada pessoa joga com uma conta só.',
  INVALID_CPF: 'CPF inválido. Confira os números.',
  INVALID_BIRTH_DATE: 'Data de nascimento inválida.',
  IDENTITY_MISMATCH:
    'Os dados não conferem com o CPF informado. Verifique o número e a data de nascimento.',
  RECEITA_UNAVAILABLE:
    'Não conseguimos conferir seus dados agora. Tente de novo mais tarde — sua conta não foi alterada.',
  KYC_NOT_PENDING: 'Sua identidade já foi analisada.',
  MINOR_BLOCKED: 'A plataforma só aceita maiores de 18 anos.',
  SELF_EXCLUDED: 'Sua conta está em autoexclusão.',
  ACCOUNT_BLOCKED: 'Conta bloqueada. Fale com o suporte.',
  DEPOSIT_LIMIT_EXCEEDED: 'Esse valor passa do seu limite diário de depósito.',
  INVALID_AMOUNT: 'Valor fora do permitido.',
  DEPOSIT_UNAVAILABLE: 'O depósito ainda não está disponível.',
  PIX_UNAVAILABLE: 'O banco não respondeu. Tente de novo em instantes.',
  NOT_YOUR_CHARGE: 'Essa cobrança não é sua.',
  CHARGE_NOT_FOUND: 'Cobrança não encontrada.',
  CPF_REQUIRED: 'Informe seu CPF antes de sacar.',
  KYC_REQUIRED: 'Sua identidade ainda não foi verificada.',
  AMOUNT_BELOW_MINIMUM: 'Valor abaixo do mínimo para saque.',
  AMOUNT_ABOVE_MAXIMUM: 'Valor acima do máximo por saque.',
  DAILY_LIMIT_EXCEEDED: 'Você já pediu o máximo de saque nas últimas 24 horas.',
  TOO_SOON: 'Espere alguns minutos antes de pedir outro saque.',
  WITHDRAWAL_IN_PROGRESS: 'Você já tem um saque em andamento.',
  INSUFFICIENT_FUNDS: 'Saldo insuficiente.',
};

/** Por que não dá para sacar, em português de quem lê. */
export const MOTIVOS_SAQUE: Record<string, string> = {
  IDENTIDADE_NAO_DECLARADA: 'Informe data de nascimento e CPF para poder sacar.',
  MENOR_DE_IDADE: 'A plataforma só aceita maiores de 18 anos.',
  CONTA_BLOQUEADA: 'Conta bloqueada. Fale com o suporte.',
  KYC_PENDENTE: 'Sua identidade ainda está em verificação. O saque abre quando ela for aprovada.',
  SAQUE_EM_ANDAMENTO: 'Você já tem um saque em andamento.',
  SALDO_ABAIXO_DO_MINIMO: 'Seu saldo ainda não chegou ao mínimo para saque.',
};

async function pedir<T>(
  caminho: string,
  init: RequestInit,
  token: string,
): Promise<Resultado<T>> {
  const base = baseDaApi();
  if (!base) return { estado: 'indisponivel', mensagem: 'API não configurada nesta build.' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}${caminho}`, {
      ...init,
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
        ...(init.headers ?? {}),
      },
    });
  } catch {
    return { estado: 'indisponivel', mensagem: 'O serviço não respondeu.' };
  }

  if (resposta.ok) return { estado: 'ok', dado: (await resposta.json()) as T };

  const corpo = (await resposta.json().catch(() => ({}))) as {
    error?: unknown;
    message?: unknown;
  };
  const codigo = typeof corpo.error === 'string' ? corpo.error : 'ERRO';
  if (resposta.status >= 500 && codigo !== 'PIX_UNAVAILABLE' && codigo !== 'DEPOSIT_UNAVAILABLE') {
    return { estado: 'indisponivel', mensagem: `O serviço respondeu ${resposta.status}.` };
  }
  return {
    estado: 'recusado',
    codigo,
    // O servidor às vezes sabe mais que o dicionário: numa divergência de
    // identidade, ele diz se foi a data ou o CPF que não bate, e isso é o
    // que a pessoa precisa para corrigir.
    mensagem:
      typeof corpo.message === 'string' && corpo.message.trim() !== ''
        ? corpo.message
        : (MOTIVOS[codigo] ?? 'Não foi possível concluir.'),
  };
}

export function carregarConta(token: string): Promise<Resultado<EstadoConta>> {
  return pedir<EstadoConta>('/api/account', { method: 'GET' }, token);
}

/**
 * Declara data de nascimento e CPF.
 *
 * Uma vez por conta: corrigir depois passa por atendimento, porque trocar
 * esses dados é também a manobra de quem quer fugir de uma autoexclusão.
 */
export function declararIdentidade(
  token: string,
  nascimento: string,
  cpf: string,
): Promise<Resultado<EstadoConta>> {
  return pedir<EstadoConta>(
    '/api/identity',
    { method: 'POST', body: JSON.stringify({ birthDate: nascimento, cpf }) },
    token,
  );
}

/**
 * Confere na Receita uma identidade que já estava declarada.
 *
 * Nada vai no corpo além da ação: o CPF conferido é o que está gravado.
 * Mandar o CPF daqui seria deixar o cliente escolher contra o que a
 * própria conta é verificada.
 */
export function verificarIdentidade(token: string): Promise<Resultado<EstadoConta>> {
  return pedir<EstadoConta>(
    '/api/identity',
    { method: 'POST', body: JSON.stringify({ action: 'VERIFY' }) },
    token,
  );
}

export function criarDeposito(token: string, centavos: number): Promise<Resultado<Cobranca>> {
  return pedir<Cobranca>(
    '/api/deposit',
    { method: 'POST', body: JSON.stringify({ amountCents: centavos }) },
    token,
  );
}

export function situacaoCobranca(
  token: string,
  txid: string,
): Promise<Resultado<SituacaoCobranca>> {
  return pedir<SituacaoCobranca>(
    `/api/deposit?txid=${encodeURIComponent(txid)}`,
    { method: 'GET' },
    token,
  );
}

export function carregarSaque(token: string): Promise<Resultado<EstadoSaque>> {
  return pedir<EstadoSaque>('/api/withdrawal', { method: 'GET' }, token);
}

/**
 * Pede o saque. Só o valor — o destino não é escolha de ninguém aqui.
 *
 * O pedido não paga: debita o saldo e entra na fila de quem decide. O
 * dinheiro sai depois, quando uma pessoa aprova e o banco confirma.
 */
export function pedirSaque(
  token: string,
  centavos: number,
): Promise<Resultado<{ readonly id: string; readonly status: string }>> {
  return pedir<{ id: string; status: string }>(
    '/api/withdrawal',
    { method: 'POST', body: JSON.stringify({ amountCents: centavos }) },
    token,
  );
}

/** Máscara de CPF enquanto se digita, sem alterar o que é enviado. */
export function formatarCpf(bruto: string): string {
  const d = bruto.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
